// Dashboard for a company being set up (prototype/admin/src/20-dashboard.js: dashboard(),
// setupSteps(), setupChecklist(), freshDashboard()). The money dashboard comes with receipts (phase 5/8).
import { useState } from 'react';
import { L, N, fDate } from '../lib/prefs';
import { go } from '../lib/route';
import { type Setup, can, setSetupHidden, useApp } from '../lib/session';
import { pageAllowed } from '../shell/nav';
import { soon } from '../shell/soon';
import { Btn, Card, Empty, FakeQR, IBtn, Ic, Meter, PageHead, Seg } from '../ui/kit';
import { toast } from '../ui/toast';

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? L('Good morning', 'صباح الخير') : h < 17 ? L('Good afternoon', 'مساء الخير') : L('Good evening', 'مساء الخير');
}

type Step = [keyof Setup, string, string, boolean, string];
export function setupSteps(s: Setup): Step[] {
  return [
    ['company', L('Add company details', 'أضف بيانات الشركة'), L('Legal name, TRN and address for the voucher.', 'الاسم القانوني والرقم الضريبي والعنوان.'), s.company, 'company'],
    ['brand', L('Add your logo', 'أضف شعارك'), L('Shown on every receipt and email.', 'يظهر على كل سند وبريد.'), s.brand, 'design'],
    ['look', L('Choose your receipt look', 'اختر شكل السند'), L('A4 portrait or the landscape paper book, with a live preview.', 'A4 عمودي أو دفتر أفقي، مع معاينة مباشرة.'), s.look, 'design'],
    ['rules', L('Check the receipt rules', 'راجع قواعد السندات'), L('Cheque photos, voids, sharing and cash handover.', 'صور الشيكات والإلغاء والمشاركة والتسليم.'), s.rules, 'rules'],
    ['customers', L('Import customers', 'استورد العملاء'), L('From Excel or CSV, or add them as you go. Open post-dated cheques can be imported on the Cheques page.', 'من إكسل أو CSV، أو أضفهم تدريجياً. الشيكات المؤجلة القائمة تُستورد من صفحة الشيكات.'), s.customers, 'customers'],
    ['people', L('Invite your team', 'ادعُ فريقك'), L('Collectors get the phone app; finance uses this panel.', 'المحصلون يستخدمون التطبيق والمالية هذه اللوحة.'), s.people, 'people'],
    ['billing', L('Add a payment card', 'أضف بطاقة دفع'), L('Billed monthly from today. Cancel any time.', 'تُحتسب الفوترة شهرياً من اليوم. ألغِ في أي وقت.'), s.billing, 'billing'],
  ];
}

function SetupChecklist() {
  const { co, setup } = useApp();
  if (!co || !setup || setup.hidden) return null;
  const steps = setupSteps(setup);
  const done = steps.filter(s => s[3]).length;
  if (done === steps.length) return null;
  const hide = async () => {
    try { await setSetupHidden(true); toast(L('Checklist hidden. Find it again in Help.', 'تم الإخفاء. تجده في المساعدة.')); }
    catch { toast(L('Couldn\'t hide the checklist. Check your connection and try again.', 'تعذر الإخفاء. تحقق من الاتصال وحاول مجدداً.'), { tone: 'bad' }); }
  };
  const name = N(co.name_en, co.name_ar);
  return (
    <section className="setup">
      <header>
        <div>
          <h2>{L('Set up ' + name, 'إعداد ' + name)}</h2>
          <p>{L(`${done} of ${steps.length} done. Your team can start issuing receipts once people are invited.`, `أُنجز ${done} من ${steps.length}. يمكن لفريقك البدء بعد دعوتهم.`)}</p>
        </div>
        <Meter v={done} max={steps.length} tone="good" />
        <IBtn icon="x" title={L('Hide checklist', 'إخفاء القائمة')} onClick={() => void hide()} />
      </header>
      <ol className="steps">
        {steps.map(([k, t, d, ok, r], i) => (
          <li key={k} className={ok ? 'done' : ''}>
            <span className="st-n">{ok ? <Ic n="check" /> : i + 1}</span>
            <div><b>{t}</b><small>{d}</small></div>
            {ok ? <span className="muted sm">{L('Done', 'تم')}</span> : pageAllowed(r) ? <Btn sm onClick={() => go(r)}>{L('Start', 'ابدأ')}</Btn> : null}
          </li>
        ))}
      </ol>
    </section>
  );
}

function FreshDashboard() {
  return (
    <div className="grid g-12">
      <Card title={L('Where the money is', 'أين الأموال')} cls="span-7">
        <Empty icon="receipt" title={L('No receipts yet', 'لا سندات بعد')}
          body={L('When your collectors issue receipts in the phone app, you\'ll see every dirham here: who holds it, what was handed over and what reached the bank.', 'عندما يصدر المحصلون سندات من التطبيق، سترى كل درهم هنا: من يحمله وما سُلّم وما وصل للبنك.')}
          action={can('issue') ? <Btn kind="primary" icon="plus" onClick={() => soon(L('Issuing receipts', 'إصدار السندات'))}>{L('Issue the first receipt from the web', 'أصدر أول سند من الويب')}</Btn> : null} />
      </Card>
      <Card title={L('Get the phone app', 'حمّل تطبيق الهاتف')} cls="span-5">
        <div className="qr-box"><FakeQR /><div><b>{L('Scan to install xReceived', 'امسح لتثبيت xReceived')}</b><span>{L('Invited collectors get a link by email too.', 'يصل المدعوين رابط بالبريد أيضاً.')}</span></div></div>
      </Card>
    </div>
  );
}

type Range = 'today' | '7' | '30' | 'month';
export function Dashboard() {
  const { co, me } = useApp();
  const [rk, setRk] = useState<Range>('30');
  if (!co || !me) return null;
  return (
    <>
      <PageHead
        title={`${greeting()}, ${me.name.split(' ')[0]}`}
        sub={<>{fDate(Date.now(), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} · {N(co.name_en, co.name_ar)} </>}
        tools={<Seg<Range> items={[['today', L('Today', 'اليوم')], ['7', L('7 days', '7 أيام')], ['30', L('30 days', '30 يوماً')], ['month', L('This month', 'هذا الشهر')]]} cur={rk} onPick={setRk} />} />
      <SetupChecklist />
      <FreshDashboard />
    </>
  );
}
